import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Stack,
  LinearProgress,
  Grid,
  Divider,
} from "@mui/material";
import {
  AccessTime,
  AttachMoney,
  CheckCircle,
  Warning,
  Update as UpdateIcon,
} from "@mui/icons-material";

interface Project {
  id: string;
  title: string;
  description?: string;
  status: "planning" | "in_progress" | "on_hold" | "complete";
  completion_percentage: number;
  budget?: number;
  start_date?: string;
  target_end_date?: string;
  contractor_name?: string;
  pending_approvals_count?: number;
  unread_updates_count?: number;
  last_update?: string;
}

interface ProjectOverviewProps {
  project: Project;
  onClick?: () => void;
}

export function ProjectOverview({ project, onClick }: ProjectOverviewProps) {
  const getStatusColor = (
    status: Project["status"],
  ): "default" | "primary" | "warning" | "success" => {
    switch (status) {
      case "planning":
        return "default";
      case "in_progress":
        return "primary";
      case "on_hold":
        return "warning";
      case "complete":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: Project["status"]): string => {
    switch (status) {
      case "planning":
        return "Planning";
      case "in_progress":
        return "In Progress";
      case "on_hold":
        return "On Hold";
      case "complete":
        return "Complete";
      default:
        return status;
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatCurrency = (amount?: number): string => {
    if (!amount) return "Not set";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getTimeAgo = (dateString?: string): string => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  return (
    <Card
      sx={{
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": onClick
          ? {
              transform: "translateY(-4px)",
              boxShadow: 6,
            }
          : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" gutterBottom>
              {project.title}
            </Typography>
            {project.contractor_name && (
              <Typography variant="body2" color="text.secondary">
                Contractor: {project.contractor_name}
              </Typography>
            )}
          </Box>
          <Chip
            label={getStatusLabel(project.status)}
            color={getStatusColor(project.status)}
            size="small"
          />
        </Box>

        {/* Description */}
        {project.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
            noWrap
          >
            {project.description}
          </Typography>
        )}

        {/* Progress */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Overall Progress
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {project.completion_percentage}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={project.completion_percentage}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Project Details */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AttachMoney sx={{ fontSize: 20, color: "text.secondary" }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Budget
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(project.budget)}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTime sx={{ fontSize: 20, color: "text.secondary" }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Target End
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDate(project.target_end_date)}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Pending Items */}
        <Stack spacing={1.5}>
          {/* Pending Approvals */}
          {project.pending_approvals_count !== undefined &&
            project.pending_approvals_count > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  bgcolor: "warning.light",
                  borderRadius: 1,
                }}
              >
                <Warning sx={{ color: "warning.dark", mr: 1 }} />
                <Typography variant="body2" fontWeight="medium">
                  {project.pending_approvals_count} approval
                  {project.pending_approvals_count > 1 ? "s" : ""} pending
                </Typography>
              </Box>
            )}

          {/* Unread Updates */}
          {project.unread_updates_count !== undefined &&
            project.unread_updates_count > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  bgcolor: "info.light",
                  borderRadius: 1,
                }}
              >
                <UpdateIcon sx={{ color: "info.dark", mr: 1 }} />
                <Typography variant="body2" fontWeight="medium">
                  {project.unread_updates_count} new update
                  {project.unread_updates_count > 1 ? "s" : ""}
                </Typography>
              </Box>
            )}

          {/* No Pending Items */}
          {(!project.pending_approvals_count ||
            project.pending_approvals_count === 0) &&
            (!project.unread_updates_count ||
              project.unread_updates_count === 0) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  bgcolor: "success.light",
                  borderRadius: 1,
                }}
              >
                <CheckCircle sx={{ color: "success.dark", mr: 1 }} />
                <Typography variant="body2" fontWeight="medium">
                  All caught up!
                </Typography>
              </Box>
            )}

          {/* Last Update */}
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Last update: {getTimeAgo(project.last_update)}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
